import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { getTranslatedModuleSlug } from "@/utils/routing";

const COPY = {
  sk: {
    label: "E-faktúra cez Peppol",
    note: "epostak — certifikovaný Access Point siete Peppol",
    aria: "Bidmio doručuje e-faktúry cez certifikovaný Access Point siete Peppol (epostak)",
  },
  cs: {
    label: "E-faktura přes Peppol",
    note: "epostak — certifikovaný Access Point sítě Peppol",
    aria: "Bidmio doručuje e-faktury přes certifikovaný Access Point sítě Peppol (epostak)",
  },
};

type Tone = "light" | "dark";

interface PeppolTrustBadgeProps {
  tone?: Tone;
  asLink?: boolean;
  showNote?: boolean;
  className?: string;
}

const PeppolTrustBadge = ({
  tone = "light",
  asLink = true,
  showNote = true,
  className = "",
}: PeppolTrustBadgeProps) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "sk") as string;

  if (lang !== "sk" && lang !== "cs") return null;
  const copy = COPY[lang as "sk" | "cs"];

  const surface =
    tone === "dark"
      ? "border-background/25 bg-background/10 hover:border-background/50"
      : "border-primary/30 bg-primary/5 hover:border-primary/60";
  const iconColor = tone === "dark" ? "text-background" : "text-primary";
  const labelColor = tone === "dark" ? "text-background" : "text-foreground";
  const noteColor = tone === "dark" ? "text-background/70" : "text-muted-foreground";

  const inner = (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 transition-colors ${surface} ${className}`}
      aria-label={copy.aria}
      data-testid="badge-peppol-trust"
    >
      <ShieldCheck className={`h-4 w-4 shrink-0 ${iconColor}`} />
      <span className={`text-xs font-semibold ${labelColor}`}>{copy.label}</span>
      {showNote && (
        <span className={`hidden sm:inline text-xs ${noteColor}`}>· {copy.note}</span>
      )}
    </span>
  );

  if (!asLink) return inner;

  const href = `/moduly/${getTranslatedModuleSlug("online-fakturacia", lang as "sk" | "cs")}/`;
  return (
    <Link to={href} className="inline-flex group" aria-label={copy.aria} data-testid="link-peppol-trust">
      {inner}
    </Link>
  );
};

export default PeppolTrustBadge;
